import React, { useState, useEffect } from "react";
import io from 'socket.io-client';

import Settings from "./Components/Settings";
import TranscriptDisplay from "./Components/TranscriptDisplay";
import ListeningIndicator from "./Components/ListeningIndicator";
import { useWebSocket } from "./Hooks/useWebSocket";
import { useAudioStreamer } from "./Hooks/useAudioStreamer";

import './App.css';

// Replace localhost with your actual backend URL 
const BACKEND_ORIGIN = "https://autocaption-backend-328210226873.us-east4.run.app";
//const BACKEND_ORIGIN = "http://localhost:8080";

const socket = io(BACKEND_ORIGIN, { transports: ['websocket'] });

function App() {

  const { isMicAvailable, transcription, interimTranscription } = useWebSocket(socket);
  const { startStreaming, stopStreaming, isRecording, listeningText } = useAudioStreamer(socket);

  const [settingsConfig, setSettingsConfig] = useState({
    layoutTheme: "light-theme",
    fontSize: 20,
    fontFamily: "Arial",
    language: "en",
    disfluencies: true,              // filler words like "oh", "umm", 
    model: "general",
    speakerDiarization: true
  });

  const [speakerLabels, setSpeakerLabels] = useState({});
  const updateSpeakerLabel = (speakerId, label) => {
    setSpeakerLabels((prev) => ({
      ...prev,
      [speakerId]: label
    }));
  };

  const applySpeakerLabels = (text) =>
    text.replace(
      /(^|\n)\s*(?:>>\s*)?(?:\[(S\d+)\]|(S\d+):)\s*/g,
      (match, lineStart, bracketSpeaker, colonSpeaker) => {
        const speakerId = bracketSpeaker || colonSpeaker;
        const displayName = speakerLabels[speakerId] || speakerId;
        return `${lineStart}${displayName}: `;
      }
    );

  const saveTranscript = () => {
    if (!transcription) return;

    // Clean up text a bit
    let cleaned = applySpeakerLabels(transcription)
      .replace(/[ \t]+/g, " ")        // collapse extra spaces
      .replace(/\n{3,}/g, "\n\n")     // max 2 line breaks
      .trim();

    const timestamp = new Date();
    const formattedTime = timestamp.toLocaleString();

    const content = `Transcript 
Generated: ${formattedTime}

----------------------------------------

${cleaned}
`;

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;

    const filenameTime = timestamp.toISOString().replace(/[:.]/g, "-");
    a.download = `transcript-${filenameTime}.txt`;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const updateSettings = (key, value) => {
    setSettingsConfig((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.className = settingsConfig.layoutTheme;
    }
  }, [settingsConfig.layoutTheme]);

  return (
    <div className="app">

      {/* Floating Settings Panel */}
      <div className="settings-overlay"
        style={{ display: (!isRecording && isMicAvailable) ? "block" : "none" }}>
        <Settings settingsConfig={settingsConfig} updateSettings={updateSettings} />

        <div className="center-button">
          <button onClick={() => startStreaming(settingsConfig)}
            className="customButton">
            Start transcribing
          </button>
        </div>
      </div>

      {isRecording && (
        <div className="live-settings-drawer">
          <div className="live-settings-panel">
            <Settings
              settingsConfig={settingsConfig}
              updateSettings={updateSettings}
              compact={true}
            />
          </div>
        </div>
      )}

      {/* Stop button: container always same height, only button hides */}
      <div className="center-button">
        <button
          onClick={stopStreaming}
          className="customButton"
          style={{ visibility: isRecording ? "visible" : "hidden" }}
        >
          Stop
        </button>
      </div>

      {/* Save transcript */}
      <div className="center-button">
        <button
          onClick={saveTranscript}
          className="customButton"
          style={{ display: (isRecording && transcription) ? "block" : "none" }}
        >
          Save Transcript
        </button>
      </div>

      {/* Listening + Transcript area */}
      <ListeningIndicator
        text={listeningText}
        style={{ visibility: isRecording ? "visible" : "hidden" }}
      />
      <div style={{
        fontSize: `${settingsConfig.fontSize}px`,
        fontFamily: settingsConfig.fontFamily
      }}>
        <TranscriptDisplay
          transcription={transcription}
          interimTranscription={interimTranscription}
          isRecording={isRecording}
          speakerLabels={speakerLabels}
          updateSpeakerLabel={updateSpeakerLabel}
        />
      </div>
    </div>

  );
}

export default App;
